import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Download } from "lucide-react";
import api, { API } from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import { toast } from "sonner";

export const LEAD_STATUSES = [
  { id: "new", label: "New Lead", dot: "bg-red-500" },
  { id: "contacted", label: "Contacted", dot: "bg-yellow-400" },
  { id: "induction_attended", label: "Induction Attended", dot: "bg-green-500" },
  { id: "community_joined", label: "Community Joined", dot: "bg-blue-500" },
  { id: "converted", label: "Converted", dot: "bg-purple-500" },
  { id: "inactive", label: "Inactive", dot: "bg-slate-500" },
];

export const StatusBadge = ({ status }) => {
  const s = LEAD_STATUSES.find((x) => x.id === status) || LEAD_STATUSES[0];
  return (
    <span className="inline-flex items-center gap-2 text-sm text-slate-200" data-testid={`status-badge-${status}`}>
      <span className={`w-2 h-2 rounded-full ${s.dot}`} /> {s.label}
    </span>
  );
};

export default function AdminLeads() {
  const [leads, setLeads] = useState([]);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const navigate = useNavigate();

  const load = useCallback(() => {
    const params = {};
    if (status) params.status = status;
    if (q) params.q = q;
    api.get("/admin/leads", { params }).then((r) => setLeads(r.data)).catch(() => toast.error("Could not load leads"));
  }, [status, q]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/export.csv", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "castles_students.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed");
    }
  };

  return (
    <AdminLayout title="Leads (CRM)">
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 text-slate-500 absolute left-4 top-1/2 -translate-y-1/2" />
          <input
            data-testid="leads-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, email, city, university..."
            className="w-full bg-white/5 border border-white/10 rounded-full pl-11 pr-5 py-3 text-sm text-white placeholder-slate-500 outline-none transition-colors duration-300 focus:border-[#D4AF37]/70"
          />
        </div>
        <select
          data-testid="leads-status-filter"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="bg-white/5 border border-white/10 rounded-full px-5 py-3 text-sm text-white outline-none [&>option]:bg-[#0A1633]"
        >
          <option value="">All statuses</option>
          {LEAD_STATUSES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
        </select>
        <button
          data-testid="export-csv-button"
          onClick={exportCsv}
          className="flex items-center gap-2 rounded-full border border-white/15 px-5 py-3 text-sm text-slate-200 transition-all duration-300 hover:border-[#D4AF37]/60 hover:text-[#D4AF37]"
        >
          <Download className="w-4 h-4" /> Export CSV
        </button>
      </div>

      <div className="rounded-3xl border border-white/10 bg-white/[0.02] overflow-x-auto" data-testid="leads-table">
        <table className="w-full text-sm min-w-[760px]">
          <thead>
            <tr className="text-left text-xs tracking-[0.15em] uppercase text-slate-500 border-b border-white/10">
              <th className="px-6 py-4">Student</th>
              <th className="px-6 py-4">Contact</th>
              <th className="px-6 py-4">UK City</th>
              <th className="px-6 py-4">University</th>
              <th className="px-6 py-4">Status</th>
              <th className="px-6 py-4">Joined</th>
            </tr>
          </thead>
          <tbody>
            {leads.map((l) => (
              <tr
                key={l.user_id}
                data-testid={`lead-row-${l.user_id}`}
                onClick={() => navigate(`/admin/students/${l.user_id}`)}
                className="border-b border-white/5 cursor-pointer transition-colors duration-200 hover:bg-white/[0.04]"
              >
                <td className="px-6 py-4 text-white font-medium">{l.first_name || l.name || "Unnamed"}</td>
                <td className="px-6 py-4 text-slate-400">
                  <div>{l.email || "-"}</div>
                  <div className="text-xs text-slate-600">{l.whatsapp ? `${l.whatsapp_country_code || ""} ${l.whatsapp}` : ""}</div>
                </td>
                <td className="px-6 py-4 text-slate-300">{l.uk_city || "-"}</td>
                <td className="px-6 py-4 text-slate-300">{l.university || "-"}</td>
                <td className="px-6 py-4"><StatusBadge status={l.lead_status} /></td>
                <td className="px-6 py-4 text-slate-500">{(l.created_at || "").slice(0, 10)}</td>
              </tr>
            ))}
            {!leads.length && (
              <tr><td colSpan={6} className="px-6 py-12 text-center text-slate-500">No students yet</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </AdminLayout>
  );
}
